import { useMemo, useRef, useState } from 'react'
import { Line } from 'react-chartjs-2'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box, Card, CardContent, Chip, ClickAwayListener, IconButton,
  MenuItem, MenuList, Paper, Popper, Skeleton, Tooltip, Typography,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import ErrorOutlineIcon       from '@mui/icons-material/ErrorOutline'
import RefreshIcon            from '@mui/icons-material/Refresh'
import OpenInFullIcon         from '@mui/icons-material/OpenInFull'
import { selectChart, selectIsLoadingChart, loadKpiData } from '../../reducers/kpiDataReducer'
import { selectKpiNamesByTech, selectKpisByTechGrouped } from '../../reducers/kpiConfigReducer'
import { setKpiSlot }                       from '../../reducers/filtersReducer'
import { applyAndFetchKpiData }             from '../../actions/kpiDataActions'
import { buildDatasets, buildOptions, TECH_BADGE, getSeriesColor } from './LockdownKPIDashboardChartConfig'
import celfinetLogo from '../../../../assets/Cyient_Slate_Std_RGB.png'
import LockdownKPIDashboardChartModal from './LockdownKPIDashboardChartModal'

const TECH_BORDER = { '5G': '#00897b', '4G': '#1565c0', '3G': '#e65100', '2G': '#ad1457' }

function cloneForChart(series) { return JSON.parse(JSON.stringify(series)) }

function CardSkeleton({ tech }) {
  return (
    <Card elevation={0} sx={{
      border: '1px solid', borderColor: 'divider', borderRadius: 3,
      borderTop: `3px solid ${TECH_BORDER[tech]}`,
      height: '100%', display: 'flex', flexDirection: 'column',
    }}>
      <CardContent sx={{ p: '12px 14px 10px !important', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, flexShrink: 0 }}>
          <Skeleton width={140} height={12} />
          <Skeleton width={28} height={18} sx={{ borderRadius: 10 }} />
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, position: 'relative', borderRadius: 1, overflow: 'hidden' }}>
          <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
        </Box>
      </CardContent>
    </Card>
  )
}

function CardError({ tech, kpiName, slot, error, onSelectKpi, kpiOptions }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const anchorRef = useRef(null)

  return (
    <Card elevation={0} sx={{
      border: '1px solid', borderColor: '#ffcdd2', borderRadius: 3,
      borderTop: `3px solid #e53935`,
      height: '100%', display: 'flex', flexDirection: 'column',
    }}>
      <CardContent sx={{ p: '12px 14px 10px !important', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Tooltip title="Click to change KPI" placement="top" arrow>
            <Box ref={anchorRef} onClick={() => setMenuOpen(v => !v)}
              sx={{
                display: 'inline-flex', alignItems: 'center', gap: '4px',
                cursor: 'pointer', px: '6px', py: '3px', borderRadius: 1,
                border: '1px solid', borderColor: menuOpen ? 'error.main' : 'transparent',
                bgcolor: menuOpen ? '#ffebee' : 'transparent',
                '&:hover': { bgcolor: '#fff5f5', borderColor: '#ffcdd2' },
                transition: 'all .15s',
              }}
            >
              <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#c62828', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {kpiName}
              </Typography>
              <KeyboardArrowDownIcon sx={{ fontSize: 13, color: '#e57373', transform: menuOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
            </Box>
          </Tooltip>
          <Chip label={tech} size="small"
            sx={{ bgcolor: '#ffebee', color: '#c62828', border: '1px solid #ef9a9a', fontWeight: 700, fontSize: 9, height: 18 }} />
        </Box>

        <Popper open={menuOpen} anchorEl={anchorRef.current} placement="bottom-start" sx={{ zIndex: 50 }}>
          <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
            <Paper elevation={4} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', maxHeight: 240, overflow: 'auto', minWidth: 220 }}>
              <Box sx={{ px: 1.5, py: '6px', borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#f8faff' }}>
                <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Select KPI — {tech}
                </Typography>
              </Box>
              <MenuList dense sx={{ py: '4px' }}>
                {Array.from(kpiOptions).map(k => (
                  <MenuItem key={k} onClick={() => { setMenuOpen(false); onSelectKpi(k) }} selected={k === kpiName}
                    sx={{ fontSize: 11, '&.Mui-selected': { color: 'primary.main', fontWeight: 600, bgcolor: 'primary.light' } }}>
                    {k}
                  </MenuItem>
                ))}
              </MenuList>
            </Paper>
          </ClickAwayListener>
        </Popper>

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, px: 2 }}>
          <ErrorOutlineIcon sx={{ fontSize: 28, color: '#ef9a9a' }} />
          <Typography sx={{ fontSize: 10, color: 'error.main', fontWeight: 600, textAlign: 'center' }}>
            Query Error
          </Typography>
          <Typography sx={{
            fontSize: 9, color: 'text.disabled', textAlign: 'center',
            fontFamily: 'IBM Plex Mono, monospace',
            maxHeight: 80, overflowY: 'auto', wordBreak: 'break-word',
          }}>
            {error}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  )
}

export default function LockdownKPIDashboardKpiCard({ tech, kpiName, slot, showMarks = true }) {
  const dispatch         = useDispatch()
  const loading          = useSelector(selectIsLoadingChart(tech, kpiName))
  const chart            = useSelector(selectChart(tech, kpiName))
  const kpiOptions       = useSelector(selectKpiNamesByTech(tech))
  const kpiGrouped       = useSelector(selectKpisByTechGrouped(tech))
  const categories       = Object.keys(kpiGrouped).sort()
  const currentCategory  = Object.entries(kpiGrouped).find(([, kpis]) => kpis.includes(kpiName))?.[0] ?? ''
  const [selCategory, setSelCategory] = useState(currentCategory)
  const filteredKpis     = kpiGrouped[selCategory] ?? kpiOptions
  const selectedEntitiesMap = useSelector(s => s.filters.selectedEntities)
  const selectedEntities    = selectedEntitiesMap[tech] ?? []
  const filters          = useSelector(s => s.filters)
  const lockdownId       = useSelector(s => s.lockdown.selected?.lockdownId)
  const comparing        = useSelector(s => s.filters.tech2 !== null)
  const unit             = chart?.unit ?? chart?.Unit ?? ''

  const [menuOpen,      setMenuOpen]      = useState(false)
  const [modalOpen,     setModalOpen]     = useState(false)
  const [tooltip,       setTooltip]       = useState(null)
  const [tooltipLocked, setTooltipLocked] = useState(false)
  const anchorRef      = useRef(null)
  const chartBoxRef    = useRef(null)
  const lockedRef      = useRef(false)
  const tooltipRef     = useRef(null)
  const justPinnedRef  = useRef(false)
  lockedRef.current  = tooltipLocked
  tooltipRef.current = tooltip

  // Memo 1 — clone raw data (expensive)
  const base = useMemo(() => {
    if (!chart?.series?.length) return null
    const plainSeries = cloneForChart(chart.series)
    return { plainSeries, plainPhases: [] }  // Lockdown has no phases
  }, [chart])

  // Memo 2 — filter + build datasets (cheap)
  const { chartData, options } = useMemo(() => {
    if (!base) return {}
    const { datasets, labels } = buildDatasets(base.plainSeries, selectedEntities, tech, comparing)
    const opts = buildOptions(showMarks ? base.plainPhases : [], labels, unit)

    opts.tooltips = {
      enabled: false, mode: 'index', intersect: false,
      custom: (model) => {
        if (lockedRef.current) return
        if (model.opacity === 0) { setTooltip(null); return }
        if (!model.dataPoints?.length) return
        const chartWidth = chartBoxRef.current?.offsetWidth ?? 400
        const side = model.caretX > chartWidth / 2 ? 'left' : 'right'
        setTooltip({
          date:   model.dataPoints[0]?.xLabel ?? '',
          x:      model.caretX,
          side,
          points: model.dataPoints.map((p) => ({
            label: datasets[p.datasetIndex]?.label ?? '',
            idx:   p.datasetIndex,
            value: p.yLabel,
          })),
        })
      },
    }

    return { chartData: { labels, datasets }, options: opts }
  }, [base, selectedEntities, showMarks, tech, kpiName, tooltipLocked, unit, comparing])

  if (loading) return <CardSkeleton tech={tech} />

  const handleRefresh = () => {
    if (!lockdownId) return
    const toCSV = (v) => { const a = Array.isArray(v) ? v : (v ? [v] : []); return a.length ? a.join(',') : null }
    dispatch(loadKpiData([{
      lockdownId,
      tech,
      kpiName,
      aggLevel:  filters.aggLevel,
      dateFrom:  filters.dateFrom,
      dateTo:    filters.dateTo,
      jvId:      toCSV(filters.jvId),
      cellName:  toCSV(filters.cellName),
      side:      toCSV(filters.side),
      polygon:   toCSV(filters.polygon),
      vendor:    toCSV(filters.vendor),
      manager:   toCSV(filters.manager),
      optimiser: toCSV(filters.optimiser),
    }]))
  }

  const handleSelectKpi = (newKpi) => {
    setMenuOpen(false)
    if (newKpi === kpiName) return
    dispatch(setKpiSlot({ tech, slot, kpiName: newKpi }))
    dispatch(applyAndFetchKpiData())
  }

  const badge = TECH_BADGE[tech] ?? TECH_BADGE['4G']

  if (chart?.error ?? chart?.Error)
    return <CardError tech={tech} kpiName={kpiName} slot={slot} error={chart?.error ?? chart?.Error} kpiOptions={kpiOptions} onSelectKpi={handleSelectKpi} />

  return (
    <><Card elevation={0} sx={{
      border: '1px solid', borderColor: 'divider', borderRadius: 3,
      borderTop: `3px solid ${TECH_BORDER[tech]}`,
      transition: 'box-shadow .2s',
      '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,.10)' },
      height: '100%', display: 'flex', flexDirection: 'column',
    }}>
      <CardContent sx={{ p: '12px 14px 10px !important', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexShrink: 0 }}>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
            <Tooltip title="Category" placement="top" arrow>
              <Chip
                label={selCategory || '—'}
                size="small"
                onClick={() => setMenuOpen(v => !v)}
                sx={{
                  height: 18, fontSize: 9, fontWeight: 600, cursor: 'pointer',
                  bgcolor: menuOpen ? 'primary.light' : '#f0f4ff',
                  color: menuOpen ? 'primary.dark' : 'text.secondary',
                  border: '1px solid', borderColor: menuOpen ? 'primary.main' : '#d0d9f0',
                  '&:hover': { bgcolor: 'primary.light', borderColor: 'primary.main' },
                  transition: 'all .15s',
                }} />
            </Tooltip>

            <Tooltip title="Click to change KPI" placement="top" arrow>
              <Box ref={anchorRef} onClick={() => setMenuOpen(v => !v)}
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: '3px',
                  cursor: 'pointer', px: '4px', py: '2px', borderRadius: 1,
                  border: '1px solid', borderColor: menuOpen ? 'primary.main' : 'transparent',
                  bgcolor: menuOpen ? 'primary.light' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover', borderColor: 'divider' },
                  transition: 'all .15s', minWidth: 0,
                }}
              >
                <Typography sx={{
                  fontSize: 10, fontWeight: 700,
                  color: menuOpen ? 'primary.main' : 'text.secondary',
                  textTransform: 'uppercase', letterSpacing: '0.06em',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {kpiName}
                </Typography>
                <KeyboardArrowDownIcon sx={{
                  fontSize: 13, flexShrink: 0,
                  color: menuOpen ? 'primary.main' : 'text.disabled',
                  transform: menuOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform .15s',
                }} />
              </Box>
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            <Tooltip title="Refresh chart" placement="top" arrow>
              <IconButton size="small" onClick={handleRefresh}
                sx={{ p: '2px', color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                <RefreshIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Maximise" placement="top" arrow>
              <IconButton size="small" onClick={() => setModalOpen(true)}
                sx={{ p: '2px', color: 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                <OpenInFullIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
            <Chip label={tech} size="small"
              sx={{ bgcolor: badge.bg, color: badge.color, border: `1px solid ${badge.border}`, fontWeight: 700, fontSize: 9, height: 18 }} />
          </Box>
        </Box>

        {/* KPI dropdown — with category tabs */}
        <Popper open={menuOpen} anchorEl={anchorRef.current} placement="bottom-start" sx={{ zIndex: 50 }}>
          <ClickAwayListener onClickAway={() => setMenuOpen(false)}>
            <Paper elevation={4} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', minWidth: 260, maxWidth: 320 }}>
              <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', p: '6px 8px', display: 'flex', flexWrap: 'wrap', gap: '4px', bgcolor: '#f8faff' }}>
                {categories.map(cat => (
                  <Chip key={cat} label={cat} size="small"
                    onClick={() => setSelCategory(cat)}
                    sx={{
                      height: 20, fontSize: 9, fontWeight: 600, cursor: 'pointer',
                      bgcolor: selCategory === cat ? 'primary.main' : '#eef1f8',
                      color: selCategory === cat ? '#fff' : 'text.secondary',
                      border: 'none',
                      '&:hover': { bgcolor: selCategory === cat ? 'primary.dark' : '#dde3f5' },
                    }} />
                ))}
              </Box>
              <MenuList dense sx={{ py: '4px', maxHeight: 200, overflow: 'auto' }}>
                {(filteredKpis).map(k => (
                  <MenuItem key={k} onClick={() => handleSelectKpi(k)} selected={k === kpiName}
                    sx={{ fontSize: 11, '&.Mui-selected': { color: 'primary.main', fontWeight: 600, bgcolor: 'primary.light' } }}>
                    {k}
                  </MenuItem>
                ))}
              </MenuList>
            </Paper>
          </ClickAwayListener>
        </Popper>

        {/* Chart area */}
        <Box
          ref={chartBoxRef}
          onMouseLeave={() => { if (!lockedRef.current) setTooltip(null) } }
          onMouseDown={() => { if (tooltipRef.current && !lockedRef.current) { lockedRef.current = true; justPinnedRef.current = true; setTooltipLocked(true)}  } }
          sx={{ flex: 1, minHeight: 0, position: 'relative' }}
        >
          {chartData?.labels?.length
            ? <>
              <Line key={chartData.datasets.length} data={chartData} options={options} />

              {/* Watermark */}
              <Box sx={{
                position: 'absolute',
                top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none', zIndex: 2,
                width: '50%',
              }}>
                <Box
                  component="img"
                  src={celfinetLogo}
                  alt=""
                  sx={{ width: '100%', height: 'auto', objectFit: 'contain', opacity: 0.05 }} />
              </Box>

              {/* Tooltip — hover to show, click chart to pin, click outside to dismiss */}
              {tooltip && (
                <ClickAwayListener onClickAway={() => { if (justPinnedRef.current) { justPinnedRef.current = false; return}  setTooltipLocked(false); setTooltip(null) } }>
                  <Box sx={{
                    position: 'absolute',
                    top: 8,
                    bottom: 8,
                    ...(tooltip.side === 'right'
                      ? { left: Math.min(tooltip.x + 12, (chartBoxRef.current?.offsetWidth ?? 400) - 212) }
                      : { right: Math.max(0, (chartBoxRef.current?.offsetWidth ?? 400) - tooltip.x + 12) }
                    ),
                    zIndex: 20,
                    pointerEvents: tooltipLocked ? 'auto' : 'none',
                    width: 200,
                    display: 'flex',
                    flexDirection: 'column',
                    filter: tooltipLocked ? 'drop-shadow(0 4px 12px rgba(0,0,0,0.18))' : 'drop-shadow(0 2px 6px rgba(0,0,0,0.10))',
                    transition: 'filter .15s',
                  }}>
                    <Paper elevation={0} sx={{
                      border: '1.5px solid',
                      borderColor: tooltipLocked ? 'primary.main' : '#d0d7e3',
                      borderRadius: 2, overflow: 'hidden',
                      bgcolor: 'background.paper',
                      display: 'flex', flexDirection: 'column',
                      maxHeight: '100%',
                    }}>
                      <Box sx={{
                        px: 1.5, py: '6px',
                        background: tooltipLocked
                          ? 'linear-gradient(90deg, #1565c0 0%, #1976d2 100%)'
                          : 'linear-gradient(90deg, #e3f2fd 0%, #f0f7ff 100%)',
                        borderBottom: '1px solid', borderColor: tooltipLocked ? '#1976d2' : '#d0e8f8',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      }}>
                        <Typography sx={{
                          fontSize: 11, fontFamily: 'IBM Plex Mono, monospace',
                          fontWeight: 700,
                          color: tooltipLocked ? '#fff' : 'primary.dark',
                        }}>
                          {tooltip.date}
                        </Typography>
                        <Typography sx={{
                          fontSize: 8, fontWeight: 700, letterSpacing: '0.06em',
                          color: tooltipLocked ? 'rgba(255,255,255,0.8)' : 'text.disabled',
                        }}>
                          {tooltipLocked ? '📌 PINNED' : 'click to pin'}
                        </Typography>
                      </Box>

                      <Box sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
                        {tooltip.points.map((p, i) => {
                          const color = getSeriesColor(p.label, p.idx)
                          const val = typeof p.value === 'number' ? p.value.toFixed(3) : '—'
                          return (
                            <Box key={i} sx={{
                              display: 'flex', alignItems: 'center', gap: '8px',
                              px: 1.5, py: '4px',
                              borderBottom: '1px solid', borderColor: '#f0f0f0',
                              '&:last-child': { borderBottom: 'none' },
                              '&:hover': { bgcolor: '#fafbff' },
                            }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: color, flexShrink: 0 }} />
                              <Typography sx={{
                                fontSize: 10, fontFamily: 'IBM Plex Mono, monospace',
                                flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                color: 'text.secondary',
                              }}>
                                {p.label || 'Cluster'}
                              </Typography>
                              <Typography sx={{
                                fontSize: 10, fontFamily: 'IBM Plex Mono, monospace',
                                fontWeight: 700, color: 'text.primary', flexShrink: 0,
                              }}>
                                {val}
                              </Typography>
                            </Box>
                          )
                        })}
                      </Box>

                      {tooltip.points.length > 5 && (
                        <Box sx={{ px: 1.5, py: '4px', borderTop: '1px solid', borderColor: '#f0f0f0', bgcolor: '#fafbff' }}>
                          <Typography sx={{ fontSize: 9, color: 'text.disabled' }}>
                            {tooltip.points.length} series
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Box>
                </ClickAwayListener>
              )}
            </>
            : <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="caption" color="text.disabled">No data</Typography>
            </Box>}
        </Box>
      </CardContent>
    </Card><LockdownKPIDashboardChartModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        tech={tech}
        kpiName={kpiName}
        chart={chart}
        showMarks={showMarks} /></>
  )
}
